import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { saveBinaryWithPicker } from "../flashtools/lib/saveAs";
import { FileCheck2, Upload, Download, Trash2, Terminal, CheckCircle2, AlertTriangle, Cpu, Play, ScanSearch, ChevronDown } from "lucide-react";

const toInt = (v) => { const s = String(v ?? "").trim().toLowerCase(); if (!s) return NaN; return s.startsWith("0x") ? parseInt(s, 16) : parseInt(s, 10); };
const hx = (v, w = 8) => "0x" + (v >>> 0).toString(16).padStart(w, "0").toUpperCase();
const hxa = (v) => "0x" + (v >>> 0).toString(16).toUpperCase();

// Checksum types reverse-engineered from "ChecksumCalculator.exe"
// (Checksum Calculator for Japanese motorcycles). VERIFIED entries were confirmed
// byte-for-byte against real stock/mod files; experimental ones need a sample.
const TYPES = {
  honda_rh850: {
    label: "Honda / Denso RH850 (32-bit)", verified: true, width: 4, endian: "big",
    defAddr: 0x5effc, regionStart: 0x4f000, mcu: "Renesas RH850", binMaker: "Denso", ecuMaker: "Honda (Denso)",
    csType: "32-bit big-endian sum · two's complement",
  },
  shindengen_rh850: {
    label: "Honda / Shindengen RH(V)850 (32-bit)", verified: true, width: 4, endian: "little", wholeFile: true,
    defAddr: 0x1afe0, mcu: "Renesas RH(V)850", binMaker: "Shindengen", ecuMaker: "Shindengen",
    csType: "32-bit little-endian sum of whole file ≡ 0",
  },
  kawasaki_denso16: {
    label: "Kawasaki / Denso 16-bit (CVN)", verified: true, width: 2, endian: "big", wholeFile: true,
    defAddr: 0x7c28, mcu: "Renesas SH7058 (Denso)", binMaker: "Denso", ecuMaker: "Kawasaki (Denso)",
    csType: "16-bit big-endian whole-file sum ≡ 0 · checksum+complement @addr",
  },
  honda_8bit: {
    label: "Honda / Keihin 8-bit", verified: true, width: 1, endian: "n/a",
    defAddr: 0x0, mcu: "Keihin PGM-FI", binMaker: "Keihin", ecuMaker: "Honda (Keihin)",
    csType: "8-bit sum of all bytes ≡ 0 (mod 256)",
  },
  mitsubishi_16bit: {
    label: "Mitsubishi 16-bit (experimental)", verified: false, width: 2, endian: "little",
    defAddr: null, mcu: "Mitsubishi (MT01)", binMaker: "Mitsubishi", ecuMaker: "Mitsubishi",
    csType: "16-bit little-endian sum ≡ 0 (mod 65536)",
  },
};

// Shindengen 384KB checksum slot is DYNAMIC (find write-key signature → 0xD0 0x07 marker →
// slot = markerPos-4). Verified: KZRM-B21/A601=0x1AFE0, KZRM-B31=0x1AFE8, K59J=0x1BF70.
const SH_WKEY_SIG = [0x11, 0x12, 0x13, 0x14, 0x15, 0x15, 0x17];
function indexOfSeq(u8, sig, from = 0) {
  outer: for (let i = from; i <= u8.byteLength - sig.length; i++) {
    for (let k = 0; k < sig.length; k++) if (u8[i + k] !== sig[k]) continue outer;
    return i;
  }
  return -1;
}
function shindengenChecksumAddr(u8) {
  const i = indexOfSeq(u8, SH_WKEY_SIG);
  if (i < 0) return null;
  for (let j = i + SH_WKEY_SIG.length; j < u8.byteLength - 6; j++) {
    if (u8[j] === 0xD0 && u8[j + 1] === 0x07) {
      let num = j;
      if ((j - ((j - 4) & ~3)) === 6) num = j - 6;
      return num - 4;
    }
  }
  return null;
}

// Compute {stored, corrected, valid, sum} for a given type + checksum address.
function compute(u8, typeKey, addr) {
  const t = TYPES[typeKey];
  const view = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  if (addr < 0 || addr + t.width > u8.byteLength) return { error: "Checksum address out of range for this file." };

  if (typeKey === "honda_8bit") {
    let rest = 0;
    for (let i = 0; i < u8.byteLength; i++) { if (i === addr) continue; rest = (rest + u8[i]) & 0xff; }
    const stored = u8[addr];
    const corrected = (0 - rest) & 0xff;
    return { stored, corrected, valid: stored === corrected, sum: rest, width: 1 };
  }
  if (typeKey === "honda_rh850") {
    let sum = 0;
    for (let o = t.regionStart; o < addr; o += 4) sum = (sum + view.getUint32(o, false)) >>> 0;
    const stored = view.getUint32(addr, false);
    const corrected = (0 - sum) >>> 0;
    return { stored, corrected, valid: stored === corrected, sum, width: 4 };
  }
  if (typeKey === "shindengen_rh850") {
    let sum = 0;
    for (let o = 0; o + 4 <= u8.byteLength; o += 4) { if (o === addr) continue; sum = (sum + view.getUint32(o, true)) >>> 0; }
    const stored = view.getUint32(addr, true);
    const corrected = (0 - sum) >>> 0;
    return { stored, corrected, valid: stored === corrected, sum, width: 4 };
  }
  if (typeKey === "kawasaki_denso16") {
    if (addr + 4 > u8.byteLength) return { error: "Checksum block (4 bytes) out of range for this file." };
    // Big-endian 16-bit sum of whole file, excluding the checksum word (addr) & complement (addr+2)
    let B = 0;
    for (let o = 0; o + 2 <= u8.byteLength; o += 2) { if (o === addr || o === addr + 2) continue; B = (B + view.getUint16(o, false)) & 0xffff; }
    const correctedC = (0x10000 - B) & 0xffff;      // complement word @addr+2 (two's complement of B)
    const corrected = (~correctedC) & 0xffff;        // checksum word @addr (one's complement of C)
    const storedW = view.getUint16(addr, false);
    const storedC = view.getUint16(addr + 2, false);
    const valid = storedW === corrected && storedC === correctedC;
    return { stored: storedW, corrected, correctedC, storedC, valid, sum: B, width: 2, kawa: true };
  }
  // mitsubishi_16bit (experimental)
  let rest = 0;
  for (let i = 0; i + 1 < u8.byteLength; i += 2) { if (i === addr) continue; rest = (rest + view.getUint16(i, true)) & 0xffff; }
  const stored = view.getUint16(addr, true);
  const corrected = (0x10000 - rest) & 0xffff;
  return { stored, corrected, valid: stored === corrected, sum: rest, width: 2 };
}

function patch(u8, typeKey, result) {
  const out = new Uint8Array(u8);
  const t = TYPES[typeKey];
  const addr = result.addr, corrected = result.corrected;
  if (typeKey === "kawasaki_denso16") {
    out[addr] = (corrected >>> 8) & 0xff; out[addr + 1] = corrected & 0xff;                 // checksum word (BE)
    out[addr + 2] = (result.correctedC >>> 8) & 0xff; out[addr + 3] = result.correctedC & 0xff; // complement (BE)
    return out;
  }
  if (t.width === 1) out[addr] = corrected & 0xff;
  else if (t.width === 4 && t.endian === "big") { out[addr] = (corrected >>> 24) & 0xff; out[addr + 1] = (corrected >>> 16) & 0xff; out[addr + 2] = (corrected >>> 8) & 0xff; out[addr + 3] = corrected & 0xff; }
  else if (t.width === 4) { out[addr] = corrected & 0xff; out[addr + 1] = (corrected >>> 8) & 0xff; out[addr + 2] = (corrected >>> 16) & 0xff; out[addr + 3] = (corrected >>> 24) & 0xff; } // little 32-bit
  else { out[addr] = corrected & 0xff; out[addr + 1] = (corrected >>> 8) & 0xff; } // little 16-bit
  return out;
}

// Try each known checksum type at its default address; strong (16/32-bit whole-file) first.
// Returns candidates with validity so the caller can pick the matching chip + checksum type.
function autoDetect(u8) {
  const order = ["kawasaki_denso16", "shindengen_rh850", "honda_rh850", "honda_8bit"];
  const cands = [];
  for (const key of order) {
    const t = TYPES[key];
    let addr = key === "honda_8bit" ? 0x0 : t.defAddr;
    if (key === "shindengen_rh850" && u8.byteLength === 0x60000) { const d = shindengenChecksumAddr(u8); if (d != null) addr = d; }
    if (addr == null || addr + t.width > u8.byteLength) continue;
    const r = compute(u8, key, addr);
    if (r && !r.error) cands.push({ key, addr, valid: !!r.valid });
  }
  return cands;
}

export default function HondaTitanChecksum() {
  return <ChecksumCalculatorInner />;
}

function ChecksumCalculatorInner() {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [u8, setU8] = useState(null);
  const [size, setSize] = useState(0);
  const [typeKey, setTypeKey] = useState("honda_8bit");
  const [addrStr, setAddrStr] = useState("0x0");
  const [result, setResult] = useState(null);
  const [log, setLog] = useState([]);
  const [dragging, setDragging] = useState(false);
  const t = TYPES[typeKey];
  const addr = toInt(addrStr);

  const addLog = (msg, type = "info") => setLog((p) => [...p, { msg, type, ts: new Date().toLocaleTimeString() }]);
  const reset = () => { setFileName(""); setU8(null); setSize(0); setResult(null); setLog([]); if (inputRef.current) inputRef.current.value = ""; };

  const pickType = (key) => { setTypeKey(key); setResult(null); const d = TYPES[key].defAddr; if (d != null) setAddrStr(hxa(d)); };

  // Auto-detect the chip/MCU + checksum type from the binary.
  const runAutoDetect = (arr) => {
    const data = arr || u8;
    if (!data) return;
    addLog("— Auto-detect: scanning for chip / checksum type —");
    const cands = autoDetect(data);
    cands.forEach((c) => addLog(`· ${TYPES[c.key].label} @${hxa(c.addr)} → ${c.valid ? "MATCH ✓" : "no match"}`, c.valid ? "ok" : "info"));
    const valid = cands.find((c) => c.valid);
    let chosen = valid;
    if (!chosen) {
      if (data.byteLength === 0x80000) chosen = { key: "kawasaki_denso16", addr: 0x7c28 };
      else if (data.byteLength === 0x60000) {
        // Shindengen 384KB has the write-key signature → dynamic slot; else Denso RH850 @0x5EFFC.
        const shAddr = shindengenChecksumAddr(data);
        if (shAddr != null) { chosen = { key: "shindengen_rh850", addr: shAddr }; addLog(`0x60000 ROM · Shindengen write-key signature found → RH(V)850 @${hxa(shAddr)} (dynamic).`, "info"); }
        else { chosen = { key: "honda_rh850", addr: 0x5effc }; addLog("0x60000 ROM · no Shindengen signature → Honda / Denso RH850 @0x5EFFC.", "info"); }
      }
      else chosen = { key: "honda_8bit", addr: 0x0 };
      addLog("No stored checksum matched — detected by chip vector / file size (run to correct).", "info");
    }
    pickType(chosen.key);
    setAddrStr(hxa(chosen.addr));
    const t2 = TYPES[chosen.key];
    addLog(`Detected chip / MCU: ${t2.mcu} · ${t2.ecuMaker}`, "ok");
    addLog(`Detected checksum: ${t2.csType} @${hxa(chosen.addr)} ${valid ? "· already VALID" : ""}`, valid ? "ok" : "info");
    return chosen;
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".bin")) { toast.error("Please select a .bin file"); return; }
    reset();
    const ab = await file.arrayBuffer();
    const arr = new Uint8Array(ab);
    setFileName(file.name); setU8(arr); setSize(arr.byteLength);
    addLog(`Loaded "${file.name}" — ${arr.byteLength.toLocaleString()} bytes (${hx(arr.byteLength)})`, "ok");
    runAutoDetect(arr);
    toast.success("File loaded & auto-detected — press Run Checksum");
  };

  const run = () => {
    if (!u8) return;
    if (Number.isNaN(addr)) { toast.error("Enter a valid checksum address (e.g. 0x0)"); return; }
    addLog(`— ${t.label} · address ${hxa(addr)} —`);
    if (!t.verified) addLog("⚠ Experimental type — verify the result against a known-good file.", "error");
    const r = compute(u8, typeKey, addr);
    if (r.error) { addLog(`✖ ${r.error}`, "error"); toast.error(r.error); return; }
    const w = r.width * 2;
    addLog(`Sum = ${hx(r.sum, w)}`);
    addLog(`Stored @${hxa(addr)} = ${hx(r.stored, w)}`);
    addLog(`Corrected = ${hx(r.corrected, w)}`);
    if (r.valid) { addLog("✔ CHECKSUM VALID — no correction needed", "ok"); toast.success("Checksum VALID"); }
    else { addLog("✖ Mismatch — needs correction", "error"); addLog(`Will patch ${hxa(addr)} → ${hx(r.corrected, w)}`, "ok"); toast.warning("Checksum corrected — download the fixed file"); }
    setResult({ ...r, addr, w });
  };

  const download = async () => {
    if (!u8 || !result || result.valid) return;
    const out = patch(u8, typeKey, result);
    const base = fileName.replace(/\.bin$/i, "");
    const ok = await saveBinaryWithPicker(out, `${base} CHECKSUM OK.bin`, { toast });
    if (ok) addLog(`Saved corrected file (${base} CHECKSUM OK.bin)`, "ok");
  };

  const csPreview = useMemo(() => {
    if (!u8 || Number.isNaN(addr) || addr + t.width > size) return "";
    return Array.from(u8.slice(addr, addr + t.width)).map((b) => b.toString(16).padStart(2, "0").toUpperCase()).join(" ");
  }, [u8, size, addr, t.width]);

  return (
    <div className="p-4 sm:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><FileCheck2 className="h-4 w-4" /> Checksum Tool</div>
        <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Checksum Calculator</h1>
        <p className="text-slate-500 text-sm mt-1">Checksum verify &amp; correct for Japanese motorcycle ECUs — pick a type, set the address, everything runs in your browser.</p>
      </div>

      {/* Dropzone */}
      <div
        data-testid="honda-checksum-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
        className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragging ? "border-cyan-400 bg-cyan-400/5" : "border-slate-700 bg-[#0c1017]"}`}
      >
        <input ref={inputRef} data-testid="honda-checksum-file-input" type="file" accept=".bin" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
        <Upload className="h-8 w-8 text-slate-500 mx-auto mb-3" />
        <p className="text-slate-300 font-semibold">Drop your <span className="font-mono-tt text-cyan-300">.bin</span> file here</p>
        <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
          <button data-testid="honda-checksum-browse-btn" onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold transition-colors">
            <Upload className="h-4 w-4" /> Browse .bin
          </button>
          {u8 && (
            <button data-testid="honda-checksum-clear-btn" onClick={reset}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold"><Trash2 className="h-4 w-4" /> Clear</button>
          )}
        </div>
      </div>

      {u8 && (
        <div className="mt-6">
          <div className="space-y-5">
            {/* Type + address + definition */}
            <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-5">
              <h2 className="font-head font-bold text-xl uppercase text-white flex items-center gap-2 mb-4"><ScanSearch className="h-5 w-5 text-cyan-400" /> Definition</h2>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500">Checksum Type</label>
                  <div className="relative mt-1.5">
                    <select data-testid="checksum-type-select" value={typeKey} onChange={(e) => pickType(e.target.value)}
                      className="w-full appearance-none bg-[#121824] border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400">
                      {Object.entries(TYPES).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
                    </select>
                    <ChevronDown className="h-4 w-4 text-slate-500 absolute right-3 top-3 pointer-events-none" />
                  </div>
                  <p className="text-[11px] font-mono-tt mt-1.5">{t.verified ? <span className="text-emerald-400">verified ✓</span> : <span className="text-amber-400">experimental — verify result</span>}</p>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500">Checksum Address (0x hex)</label>
                  <input data-testid="checksum-address-input" value={addrStr} onChange={(e) => { setAddrStr(e.target.value); setResult(null); }} placeholder="0x0"
                    className="w-full mt-1.5 bg-[#121824] border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 text-sm font-mono-tt focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                  <p className="text-[11px] font-mono-tt text-slate-500 mt-1.5">width {t.width}B · {t.endian === "n/a" ? "8-bit" : t.endian + "-endian"}</p>
                </div>
              </div>
              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm font-mono-tt">
                <Row label="MCU Type" value={t.mcu} testid="checksum-info-mcu" accent />
                <Row label="BIN Maker" value={t.binMaker} testid="checksum-info-binmaker" />
                <Row label="ECU Maker" value={t.ecuMaker} testid="checksum-info-ecumaker" />
                <Row label="File Size" value={`${size.toLocaleString()} bytes (${hx(size)})`} testid="checksum-info-size" />
                <Row label="Checksum Type" value={t.csType} testid="checksum-info-type" />
                <Row label="Checksum Address" value={Number.isNaN(addr) ? "—" : hxa(addr)} testid="checksum-info-address" accent />
                <Row label="Raw bytes @ address" value={csPreview} testid="checksum-info-rawbytes" />
              </dl>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <button data-testid="honda-checksum-run-btn" onClick={run} disabled={Number.isNaN(addr)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-slate-950 font-bold disabled:opacity-40 transition-colors">
                  <Play className="h-4 w-4" /> Run Checksum
                </button>
                <button data-testid="honda-checksum-autodetect-btn" onClick={() => runAutoDetect()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 font-bold transition-colors">
                  <ScanSearch className="h-4 w-4" /> Auto-Detect
                </button>
              </div>
            </div>

            {result && (
              <div data-testid="honda-checksum-result" className={`rounded-xl border p-5 ${result.valid ? "border-emerald-700/50 bg-emerald-950/20" : "border-red-700/50 bg-red-950/20"}`}>
                <div className="flex items-center gap-3 mb-4">
                  {result.valid ? <CheckCircle2 className="h-7 w-7 text-emerald-400" /> : <AlertTriangle className="h-7 w-7 text-cyan-300" />}
                  <div>
                    <div data-testid="honda-checksum-status" className={`font-head font-black text-2xl uppercase ${result.valid ? "text-emerald-400" : "text-cyan-300"}`}>{result.valid ? "Checksum Valid" : "Checksum Corrected"}</div>
                    <div className="text-xs text-slate-400">{result.valid ? "Stored checksum already matches — no changes needed." : "Stored checksum is wrong — download the corrected file."}</div>
                  </div>
                </div>
                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm font-mono-tt">
                  <Row label="Sum" value={hx(result.sum, result.w)} />
                  <Row label="Address" value={hxa(result.addr)} />
                  <Row label="Old checksum" value={hx(result.stored, result.w)} testid="honda-old-checksum" />
                  <Row label="Corrected checksum" value={hx(result.corrected, result.w)} testid="honda-corrected-checksum" accent />
                </dl>
                {!result.valid && (
                  <button data-testid="honda-checksum-download-btn" onClick={download}
                    className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold">
                    <Download className="h-4 w-4" /> Download Corrected .bin
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, testid, accent }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-slate-500">{label}</dt>
      <dd data-testid={testid} className={`mt-0.5 break-all ${accent ? "text-cyan-300 font-bold" : "text-slate-200"}`}>{value || "—"}</dd>
    </div>
  );
}
